/**
 * Per-theme CSS for the chessground wrapper. Each theme overrides:
 *   - the chequer pattern (cg-board background — chessground does NOT tag
 *     squares with `.light`/`.dark` classes; the pattern is drawn entirely
 *     by an inline SVG background on cg-board, same approach as lichess's
 *     own brown/blue/green themes)
 *   - last-move highlight: TWO shades — startColor on the from-square,
 *     endColor on the to-square (plus a border on the to-square). chessground
 *     normally uses a single `last-move` class for both squares, so we tag
 *     each square with our own class via `highlight.custom` (a SquareClasses
 *     Map) — see `lastMoveHighlight` in helpers.ts and the call sites in
 *     index.web.tsx.
 *   - selected-square highlight
 *   - king-in-check radial highlight (matches the package's red radial)
 *   - coordinate colours: each label takes the OPPOSITE square colour, so it
 *     reads dark on a light square and light on a dark one (see COORDS_CSS)
 *
 * Themes are activated by setting className `circlechess-theme-<name>` on the
 * cg-wrap container. Source colors mirror `CCColors.ChessBoard.*`.
 */
import CCColors from '@/Utils/CCColors';

type Theme = {
  className: string;
  light: string;
  dark: string;
  /** background color for the from-square of the last move */
  lastMoveFrom: string;
  /** background color for the to-square of the last move */
  lastMoveTo: string;
};

const THEMES: Theme[] = [
  {
    className: 'circlechess-theme-default',
    light: CCColors.ChessBoard.Light,
    // The package uses CCColors.Progress.Bar (== #D1AB41) as the default dark.
    // CCColors.ChessBoard.Dark is the same value with different case.
    dark: CCColors.ChessBoard.Dark,
    lastMoveFrom: CCColors.ChessBoard.StartColor,
    lastMoveTo: CCColors.ChessBoard.EndColor,
  },
  {
    className: 'circlechess-theme-green-white',
    light: CCColors.ChessBoard.GreenLightColor,
    dark: CCColors.ChessBoard.GreenDarkColor,
    lastMoveFrom: CCColors.ChessBoard.GreenStartColor,
    lastMoveTo: CCColors.ChessBoard.GreenEndColor,
  },
  {
    className: 'circlechess-theme-blue-white',
    light: CCColors.ChessBoard.BlueLightColor,
    dark: CCColors.ChessBoard.BlueDarkColor,
    lastMoveFrom: CCColors.ChessBoard.BlueStartColor,
    lastMoveTo: CCColors.ChessBoard.BlueEndColor,
  },
];

const CHECK_RADIAL = 'radial-gradient(transparent 60%, #EB5757)';

/**
 * Build an SVG data-URI that draws a 2×2 chequer tile. Tiled at 25%×25% of
 * cg-board, this produces an 8×8 board where a8 (top-left when white plays)
 * is light — the standard chess-board orientation.
 *
 * The tile is laid out as:
 *   [ light, dark  ]
 *   [ dark,  light ]
 * The light cells are TRANSPARENT in the SVG, so cg-board's `background-color`
 * shows through.
 */
function chequerBg(dark: string): string {
  const encDark = encodeURIComponent(dark);
  // Single quotes inside the SVG avoid having to encode double quotes.
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2 2' " +
    "shape-rendering='crispEdges'>" +
    `<rect x='1' width='1' height='1' fill='${encDark}'/>` +
    `<rect y='1' width='1' height='1' fill='${encDark}'/>` +
    '</svg>';
  return `url("data:image/svg+xml;utf8,${svg}")`;
}

// Legal-move destination indicators (the small green dot on an empty target,
// or a green ring around a capturable opponent piece). chessground emits
// `square.move-dest` and `square.oc.move-dest` (oc = "occupied") classes
// when a piece is selected and `movable.showDests` is true, but only ships
// the actual visual in its built-in brown/blue/green theme CSS — which we
// don't load. Same shape as lichess's brown theme so the dots match user
// expectation. Theme-independent (same green across all CC themes).
const MOVE_DEST_CSS = `
cg-board square.move-dest {
  background: radial-gradient(rgba(20, 85, 30, 0.5) 22%, #208530 0, rgba(0, 0, 0, 0.3) 0, rgba(0, 0, 0, 0) 0);
}
cg-board square.oc.move-dest {
  background: radial-gradient(transparent 0%, transparent 80%, rgba(20, 85, 0, 0.3) 80%);
}
cg-board square.move-dest:hover {
  background: rgba(20, 85, 30, 0.3);
}
cg-board square.premove-dest {
  background: radial-gradient(rgba(176, 30, 30, 0.5) 22%, #B01E1E 0, rgba(0, 0, 0, 0.3) 0, rgba(0, 0, 0, 0) 0);
}
cg-board square.oc.premove-dest {
  background: radial-gradient(transparent 0%, transparent 80%, rgba(176, 30, 30, 0.2) 80%);
}
cg-board square.premove-dest:hover {
  background: rgba(176, 30, 30, 0.2);
}
`;

// Premove highlights are theme-independent (red across all themes) — visually
// distinct from confirmed last-move highlights (yellow/green) and selected-
// square highlights (green tint). See enqueuePremove / buildHighlightMap in
// index.web.tsx for how these classes get assigned.
const PREMOVE_CSS = `
cg-board square.cc-premove-from {
  background-color: rgba(176, 30, 30, 0.4);
}
cg-board square.cc-premove-to {
  background-color: rgba(176, 30, 30, 0.4);
  box-shadow: inset 0 0 0 2px rgb(175, 60, 60);
}
`;

// Touch-and-move rule: when the user has "touched" a piece (PlayChess
// `restrictedPiece` is set), highlight that square with a green tint so the
// user remembers which piece they're locked into moving. Color matches the
// legacy chessboard-package (rgba(20, 85, 35, 0.3) on
// Chessboard.js:476-478). Placed AFTER premove CSS so on a square that has
// both classes (e.g. the user queued a premove of the restricted piece),
// the restricted highlight wins — that information is more important
// because it tells the user the move is mandatory.
const RESTRICTED_PIECE_CSS = `
cg-board square.cc-restricted-piece {
  background-color: rgba(20, 85, 35, 0.3);
}
`;

// Board coordinates. chessground's base CSS places them with fixed pixel
// offsets (`top: -20px`, `left: 24px`, `bottom: -4px`) tuned for lichess's
// board size, so on any larger board they drift onto the grid lines and out
// past the edge. Instead pin them INSIDE the edge squares: rank numbers in the
// top-left of the left-hand column, file letters in the bottom-right of the
// bottom row, both in percentages of the board so they track every size.
// Text and padding scale with `--cc-board-size`, the snapped board width that
// index.web.tsx's snap effect sets on the outer div (400px until it runs).
const COORDS_CSS = `
.cg-wrap coords {
  --cc-coord-unit: calc(var(--cc-board-size, 400px) / 100);
  opacity: 1;
  font-weight: 600;
  font-size: clamp(10px, calc(var(--cc-coord-unit) * 2.6), 24px);
  line-height: 1;
}
.cg-wrap coords coord {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
}
.cg-wrap coords.ranks,
.cg-wrap coords.ranks.left {
  top: 0;
  left: 0;
  width: 12.5%;
  height: 100%;
  align-items: stretch;
}
.cg-wrap coords.ranks coord {
  transform: none;
  text-align: left;
  padding: calc(var(--cc-coord-unit) * 0.6) 0 0 calc(var(--cc-coord-unit) * 0.7);
}
.cg-wrap coords.files {
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  align-items: flex-end;
  text-transform: none;
}
.cg-wrap coords.files coord {
  text-align: right;
  padding: 0 calc(var(--cc-coord-unit) * 0.7) calc(var(--cc-coord-unit) * 0.6) 0;
}
`;

/**
 * Label colour per square. With white at the bottom the left column is the
 * a-file and the bottom row is rank 1, and the 1st label of each list (rank 1
 * / file a) sits on a1, a dark square — so odd labels are on dark squares.
 * With black at the bottom (`.black`) the edge squares are h-file / rank 8,
 * whose 1st label (rank 1 on h1, file a on a8) is on a light square, so the
 * parity flips.
 */
function coordColorCss(className: string, onLight: string, onDark: string) {
  return `
.${className} coords coord:nth-child(odd) {
  color: ${onDark};
}
.${className} coords coord:nth-child(even) {
  color: ${onLight};
}
.${className} coords.black coord:nth-child(odd) {
  color: ${onLight};
}
.${className} coords.black coord:nth-child(even) {
  color: ${onDark};
}
`;
}

// ── PlayChess V2 "Premium Themes" boards (additive, M1a) ──────────────────
//
// The three themes above paint FLAT squares, so their chequer tile is a 2x2
// SVG whose dark cells are solid and whose light cells are transparent (the
// cg-board background-color shows through). The two Gallery themes give every
// square its own material (ivory marble veining / frosted sage glass, and the
// slate-vs-ink pair in the dark variant), which a background-color cannot
// express — so their tile paints all four cells and carries the gradients as
// SVG gradient defs in `objectBoundingBox` units, which means one def is
// reused by every square at every board size.
//
// The values are the design tokens verbatim (`--board-light`, `--board-dark`,
// `--board-hi-*`, `--piece-*` of the Premium Themes study); CSS angles are
// converted to SVG endpoints with dir = (sin θ, -cos θ) (y grows downwards).

type GradientStop = {at: number; color: string; opacity: number};

const enc = (value: string): string => encodeURIComponent(value);

const svgStops = (list: GradientStop[]): string =>
  list
    .map(
      s =>
        `<stop offset='${s.at}' stop-color='${enc(s.color)}' ` +
        `stop-opacity='${s.opacity}'/>`,
    )
    .join('');

const round = (n: number): string => String(Math.round(n * 10000) / 10000);

/** CSS `<angle>deg` -> the endpoints of an objectBoundingBox linearGradient. */
function linearDef(id: string, deg: number, list: GradientStop[]): string {
  const rad = (deg * Math.PI) / 180;
  const dx = Math.sin(rad) / 2;
  const dy = -Math.cos(rad) / 2;
  return (
    `<linearGradient id='${id}' x1='${round(0.5 - dx)}' y1='${round(
      0.5 - dy,
    )}' x2='${round(0.5 + dx)}' y2='${round(0.5 + dy)}'>` +
    `${svgStops(list)}</linearGradient>`
  );
}

/** CSS `radial-gradient(... at cx cy, ...)` -> an objectBoundingBox radial. */
function radialDef(
  id: string,
  cx: number,
  cy: number,
  r: number,
  list: GradientStop[],
): string {
  return (
    `<radialGradient id='${id}' cx='${cx}' cy='${cy}' r='${r}'>` +
    `${svgStops(list)}</radialGradient>`
  );
}

/** one `<rect>` per background layer, painted back-to-front (CSS is the reverse). */
const cellRects = (x: number, y: number, paints: string[]): string =>
  paints
    .map(p => `<rect x='${x}' y='${y}' width='1' height='1' ${p}/>`)
    .join('');

function materialBg(
  defs: string,
  lightPaints: string[],
  darkPaints: string[],
): string {
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2 2' " +
    "shape-rendering='crispEdges'>" +
    `<defs>${defs}</defs>` +
    cellRects(0, 0, lightPaints) +
    cellRects(1, 1, lightPaints) +
    cellRects(1, 0, darkPaints) +
    cellRects(0, 1, darkPaints) +
    '</svg>';
  return `url("data:image/svg+xml;utf8,${svg}")`;
}

const fill = (color: string, opacity?: number): string =>
  `fill='${enc(color)}'${opacity === undefined ? '' : ` opacity='${opacity}'`}`;

const fillRef = (id: string): string => `fill='url(%23${id})'`;

// Light "Gallery": --board-light / --board-dark of the L1 palette.
const GALLERY_DEFS =
  radialDef('ccg-sheen', 0.15, 0, 1.4, [
    {at: 0, color: '#FFFFFF', opacity: 0.8},
    {at: 0.55, color: '#FFFFFF', opacity: 0},
  ]) +
  linearDef('ccg-vein1', 118, [
    {at: 0.4, color: '#786C5C', opacity: 0},
    {at: 0.46, color: '#786C5C', opacity: 0.1},
    {at: 0.52, color: '#786C5C', opacity: 0},
  ]) +
  linearDef('ccg-vein2', 32, [
    {at: 0.62, color: '#786C5C', opacity: 0},
    {at: 0.66, color: '#786C5C', opacity: 0.07},
    {at: 0.71, color: '#786C5C', opacity: 0},
  ]) +
  linearDef('ccg-glass', 155, [
    {at: 0, color: '#FFFFFF', opacity: 0.38},
    {at: 0.45, color: '#FFFFFF', opacity: 0.06},
    {at: 0.62, color: '#FFFFFF', opacity: 0},
  ]);

const GALLERY_BG = materialBg(
  GALLERY_DEFS,
  [
    fill('#EDE9E0'),
    fillRef('ccg-vein2'),
    fillRef('ccg-vein1'),
    fillRef('ccg-sheen'),
  ],
  [fill('#A4B1A3'), fill('#283C30', 0.06), fillRef('ccg-glass')],
);

// Dark "Gallery": D1 palette. The 1 px vertical hairlines of the CSS token
// (`repeating-linear-gradient(90deg, … 0 1px, … 1px 3px)`) are dropped: the
// tile is scaled to the board, so a 1 px stripe would smear — the 160deg
// stone gradient carries the same reading.
const GALLERY_DARK_DEFS =
  linearDef('ccgd-stone', 160, [
    {at: 0, color: '#4B5158', opacity: 1},
    {at: 1, color: '#3A3F45', opacity: 1},
  ]) +
  radialDef('ccgd-sheen', 0.3, 0, 1.2, [
    {at: 0, color: '#FFFFFF', opacity: 0.045},
    {at: 0.6, color: '#FFFFFF', opacity: 0},
  ]);

const GALLERY_DARK_BG = materialBg(
  GALLERY_DARK_DEFS,
  [fillRef('ccgd-stone')],
  [fill('#1E2023'), fillRef('ccgd-sheen')],
);

type MaterialTheme = {
  className: string;
  /** background-color fallback while the tile decodes */
  base: string;
  backgroundImage: string;
  /**
   * `--board-hi-light` / `--board-hi-dark` are the SAME bronze at two alphas
   * over the two square colours. chessground cannot tell us which colour a
   * highlighted square is, so the bronze is applied as a translucent overlay
   * instead of a pre-composited solid: the material stays visible and both
   * square colours land on the token's value.
   */
  highlightFrom: string;
  highlightTo: string;
  highlightRing: string;
  pieceWhiteFilter: string;
  pieceBlackFilter: string;
  /** coordinate label colour on a light / dark square */
  coordOnLight: string;
  coordOnDark: string;
};

const MATERIAL_THEMES: MaterialTheme[] = [
  {
    className: 'circlechess-theme-gallery',
    base: '#EDE9E0',
    backgroundImage: GALLERY_BG,
    highlightFrom: 'rgba(160, 125, 79, 0.30)',
    highlightTo: 'rgba(160, 125, 79, 0.38)',
    highlightRing: 'rgba(160, 125, 79, 0.75)',
    pieceWhiteFilter:
      'drop-shadow(0 2px 1.5px rgba(40, 32, 22, 0.30)) drop-shadow(0 8px 9px rgba(40, 32, 22, 0.16))',
    pieceBlackFilter:
      'drop-shadow(0 2px 1.5px rgba(40, 32, 22, 0.35)) drop-shadow(0 8px 9px rgba(40, 32, 22, 0.18))',
    coordOnLight: '#7A8A79',
    coordOnDark: '#EDE9E0',
  },
  {
    className: 'circlechess-theme-gallery-dark',
    base: '#41464C',
    backgroundImage: GALLERY_DARK_BG,
    highlightFrom: 'rgba(205, 181, 144, 0.24)',
    highlightTo: 'rgba(205, 181, 144, 0.30)',
    highlightRing: 'rgba(205, 181, 144, 0.70)',
    pieceWhiteFilter:
      'drop-shadow(0 3px 2px rgba(0, 0, 0, 0.55)) drop-shadow(0 9px 10px rgba(0, 0, 0, 0.35))',
    pieceBlackFilter:
      'drop-shadow(0 3px 2px rgba(0, 0, 0, 0.6)) drop-shadow(0 9px 10px rgba(0, 0, 0, 0.35))',
    coordOnLight: '#1E2023',
    coordOnDark: '#8A9098',
  },
];

const MATERIAL_CSS = MATERIAL_THEMES.map(
  t => `
.${t.className} cg-board {
  background-color: ${t.base};
  background-image: ${t.backgroundImage};
  background-size: 25% 25%;
}
.${t.className} cg-board square.cc-last-move-from {
  background-color: ${t.highlightFrom};
}
.${t.className} cg-board square.cc-last-move-to {
  background-color: ${t.highlightTo};
  box-shadow: inset 0 0 0 2px ${t.highlightRing};
}
.${t.className} cg-board square.selected {
  background-color: rgba(20, 85, 30, 0.5);
}
.${t.className} cg-board square.check {
  background: ${CHECK_RADIAL};
}
.${t.className} piece.white {
  filter: ${t.pieceWhiteFilter};
}
.${t.className} piece.black {
  filter: ${t.pieceBlackFilter};
}
${coordColorCss(t.className, t.coordOnLight, t.coordOnDark)}`,
).join('\n');

/**
 * Every value `colorTheme` accepts (see `themeClassName` in helpers.ts).
 * Anything else falls back to 'default'.
 */
export const THEME_NAMES = [
  'default',
  'green-white',
  'blue-white',
  'gallery',
  'gallery-dark',
] as const;

export const THEME_CSS =
  THEMES.map(
    t => `
.${t.className} cg-board {
  background-color: ${t.light};
  background-image: ${chequerBg(t.dark)};
  background-size: 25% 25%;
}
.${t.className} cg-board square.cc-last-move-from {
  background-color: ${t.lastMoveFrom};
}
.${t.className} cg-board square.cc-last-move-to {
  background-color: ${t.lastMoveTo};
  box-shadow: inset 0 0 0 2px rgb(120, 113, 64);
}
.${t.className} cg-board square.selected {
  background-color: rgba(20, 85, 30, 0.5);
}
.${t.className} cg-board square.check {
  background: ${CHECK_RADIAL};
}
${coordColorCss(t.className, t.dark, t.light)}`,
  ).join('\n') +
  MATERIAL_CSS +
  COORDS_CSS +
  MOVE_DEST_CSS +
  PREMOVE_CSS +
  RESTRICTED_PIECE_CSS;
